import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faPlus, 
  faFileInvoice, 
  faUser, 
  faChartBar, 
  faCog, 
  faUsers, 
  faList, 
  faBox, 
  faCalculator, 
  faFileAlt, 
  faFlask, 
  faDollarSign, 
  faHandshake, 
  faCalendar, 
  faBuilding, 
  faSlidersH,
  faBook,
  faChartLine,
  faRoad,
  faAddressBook,
  faCode,
  faLink,
  faTable
} from '@fortawesome/free-solid-svg-icons'
import { useAuth } from '../../contexts'
import './Navbar.css'

interface NavItem {
  path: string
  label: string
  icon?: any
  adminOnly?: boolean
  submenu?: NavItem[]
}

const navItems: NavItem[] = [
  { path: '/quotes/create', label: 'Mgd Svc Quote', icon: faPlus },
  { path: '/quotes/ad-hoc', label: 'PS Quote ', icon: faTable },
  { path: '/quotes', label: 'Saved Quotes', icon: faFileInvoice },
  { path: '/links', label: 'Links', icon: faLink },
  { path: '/roadmap', label: 'Roadmap', icon: faRoad },
  { path: '/reports', label: 'Reports', icon: faChartBar, adminOnly: true },
  { path: '/profile', label: 'Profile', icon: faUser },
      {
        path: '/admin',
        label: 'Admin',
        icon: faCog,
        adminOnly: true,
        submenu: [
              { path: '/admin/users', label: 'User Management', icon: faUsers },
              { path: '/admin/service-lines', label: 'Service Lines', icon: faList },
              { path: '/admin/service-line-items', label: 'Service Line Items', icon: faBox },
              { path: '/admin/service-line-item-pricing-rules', label: 'Service Line Item Pricing Rules', icon: faCalculator },
              { path: '/admin/templates', label: 'Templates', icon: faFileAlt },
              {
                label: 'Settings',
                icon: faSlidersH,
                submenu: [
                  { path: '/admin/pricing-rules', label: 'Pricing Management', icon: faDollarSign },
                  { path: '/admin/billing-arrangements', label: 'Billing Arrangements', icon: faHandshake },
                  { path: '/admin/billing-frequencies', label: 'Billing Frequencies', icon: faCalendar },
                  { path: '/admin/billing-entities', label: 'Billing Entities', icon: faBuilding },
                  { path: '/admin/document-contacts', label: 'Document Contacts', icon: faAddressBook },
                  { path: '/admin/static-variables', label: 'Static Variables', icon: faCode },
                  { path: '/admin/links', label: 'Link Management', icon: faLink },
                  { path: '/admin/app-settings', label: 'App Settings', icon: faSlidersH }
                ]
              },
              {
                path: '/admin/documentation',
                label: 'Documentation',
                icon: faBook,
                submenu: [
                  { path: '/admin/documentation/template-guide', label: 'Template Guide', icon: faBook },
                  { path: '/admin/service-line-documentation', label: 'Service Line Guide', icon: faBook },
                  { path: '/admin/documentation/pricing-guide', label: 'Pricing Guide', icon: faDollarSign }
                ]
              },
              { path: '/admin/billing-entity-report', label: 'Billing Entity Report', icon: faChartLine }
        ]
      },
]

export const Navbar: React.FC = () => {
  const location = useLocation()
  const { user } = useAuth()
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const navbarRef = useRef<HTMLElement>(null)

  const handleMouseEnter = (itemPath: string) => {
    setActiveDropdown(itemPath)
  }

  const handleMouseLeave = () => {
    setActiveDropdown(null)
  }

  const handleItemClick = (item: NavItem, e: React.MouseEvent) => {
    if (item.submenu) {
      e.preventDefault() // Prevent navigation
      setActiveDropdown(activeDropdown === item.path ? null : item.path)
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const isActiveItem = (item: NavItem) => {
    if (item.submenu) {
      return item.submenu.some(subItem => {
        if (subItem.submenu) {
          // Handle nested submenus
          return subItem.submenu.some(nestedItem => location.pathname === nestedItem.path) ||
                 location.pathname === subItem.path
        }
        return location.pathname === subItem.path
      }) || location.pathname === item.path
    }
    return location.pathname === item.path
  }

  return (
    <nav className="navbar" ref={navbarRef}>
      <div className="navbar__container">
        <ul className="navbar__list">
          {navItems.map((item) => {
            // Hide admin-only items if user is not admin
            if (item.adminOnly && user?.role?.name !== 'admin') {
              return null
            }
            
            return (
              <li 
                key={item.path} 
                className={`navbar__item ${activeDropdown === item.path ? 'active' : ''}`}
                onMouseEnter={() => item.submenu && handleMouseEnter(item.path)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={item.path}
                  className={`navbar__link ${
                    isActiveItem(item) ? 'navbar__link--active' : ''
                  }`}
                  onClick={(e) => handleItemClick(item, e)}
                >
                  {item.icon && <FontAwesomeIcon icon={item.icon} className="navbar__icon" />}
                  <span className="navbar__label">{item.label}</span>
                  {item.submenu && (
                    <span className="navbar__dropdown-arrow">▼</span>
                  )}
                </Link>
                
                {item.submenu && activeDropdown === item.path && (
                  <ul className="navbar__submenu">
                    {item.submenu.map((subItem) => (
                      <li key={subItem.path} className="navbar__submenu-item">
                        {subItem.submenu ? (
                          // Handle nested submenu
                          <div className="navbar__submenu-nested">
                            <div className="navbar__submenu-nested-header">
                              <div className="navbar__submenu-nested-content">
                                {subItem.icon && <FontAwesomeIcon icon={subItem.icon} className="navbar__submenu-icon" />}
                                {subItem.label}
                              </div>
                              <span className="navbar__submenu-nested-arrow">▶</span>
                            </div>
                            <ul className="navbar__submenu-nested-list">
                              {subItem.submenu.map((nestedItem) => (
                                <li key={nestedItem.path} className="navbar__submenu-nested-item">
                                  <Link
                                    to={nestedItem.path}
                                    className={`navbar__submenu-nested-link ${
                                      location.pathname === nestedItem.path ? 'navbar__submenu-nested-link--active' : ''
                                    }`}
                                  >
                                    {nestedItem.icon && <FontAwesomeIcon icon={nestedItem.icon} className="navbar__submenu-icon" />}
                                    {nestedItem.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          // Regular submenu item
                          <Link
                            to={subItem.path}
                            className={`navbar__submenu-link ${
                              location.pathname === subItem.path ? 'navbar__submenu-link--active' : ''
                            }`}
                          >
                            {subItem.icon && <FontAwesomeIcon icon={subItem.icon} className="navbar__submenu-icon" />}
                            {subItem.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </nav>
  )
}
